import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  RouteRecordRaw,
} from "vue-router";
import store from "@/store";
import { Mutations, Actions } from "@/store/enums/StoreEnums";
import { UserAuthorization } from "@/store/modules/AccountModule";
import Statistics from "@/components/dashboard/iot/Statistics.vue";
import DeviceManagement from "@/components/dashboard/iot/DeviceManagement.vue";
import IotSettings from "@/components/dashboard/iot/IotSettings.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/dashboard",
    meta: { requiresAuth: true },
    component: () => import("@/layout/Layout.vue"),
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        meta: { requiresAdmin: true },
        // component: () => import("@/views/Dashboard.vue"),
        component: () => import("@/views/dashboard/account/User.vue"),
      },
      {
        path: "/account",
        redirect: "/account/user",
        meta: { requiresAdmin: true },
        children: [
          {
            path: "/account/user",
            name: "user",
            component: () => import("@/views/dashboard/account/User.vue"),
          },
          {
            path: "/account/superteam",
            name: "superteam",
            component: () => import("@/views/dashboard/account/Superteam.vue"),
          },
          {
            path: "/account/admin",
            name: "admin",
            component: () => import("@/views/dashboard/account/Admin.vue"),
          },
          {
            path: "/account/activity",
            name: "user-activity",
            component: () =>
              import("@/views/dashboard/account/UserActivityAnalytics.vue"),
          },
        ],
      },
      {
        path: "/farm",
        redirect: "/farm/harvest",
        children: [
          {
            path: "/farm/harvest",
            name: "harvest",
            component: () => import("@/views/dashboard/farm/Harvest.vue"),
          },
          {
            path: "/farm/incoming",
            name: "incoming-harvest",
            component: () =>
              import("@/views/dashboard/farm/IncomingHarvest.vue"),
          },
          {
            path: "/farm/shipment",
            name: "shipment",
            component: () => import("@/views/dashboard/farm/Shipment.vue"),
          },
          {
            path: "/farm/upgrade",
            name: "upgrade",
            meta: { requiresAdmin: true },
            component: () => import("@/views/dashboard/farm/Upgrade.vue"),
          },
          {
            path: "/farm/price",
            name: "price",
            meta: { requiresAdmin: true },
            component: () => import("@/views/dashboard/farm/Price.vue"),
          },
        ],
      },
      {
        path: "/rbw",
        redirect: "/rbw/building",
        children: [
          {
            path: "/rbw/building",
            name: "building",
            component: () => import("@/views/dashboard/farm/Building.vue"),
          },
        ],
      },
      {
        path: "/iot",
        redirect: "/iot/statistics",
        // Seluruh IoT khusus admin; SuperTeam dialihkan ke daftar panen.
        meta: { requiresAdmin: true },
        children: [
          {
            path: "/iot/statistics",
            name: "iot-statistics",
            component: Statistics,
          },
          {
            path: "/iot/v1/statistics",
            name: "iot-statistics-v1",
            component: Statistics,
          },
          {
            path: "/iot/devices",
            name: "iot-devices",
            component: DeviceManagement,
            meta: {
              requiresAdmin: true,
              title: "Perangkat IoT",
              breadcrumbs: ["Dashboard", "IoT", "Perangkat"],
            },
          },
          {
            // Legacy path — merged into /iot/devices
            path: "/iot/device-management",
            redirect: "/iot/devices",
            meta: { requiresAdmin: true },
          },
          {
            path: "/iot/settings",
            name: "iot-settings",
            component: IotSettings,
            meta: {
              requiresAdmin: true,
              title: "Pengaturan IoT",
              breadcrumbs: ["Dashboard", "IoT", "Pengaturan"],
            },
          },
          {
            path: "/iot/report",
            name: "iot-report",
            component: () =>
              import("@/views/dashboard/iot/ReportManagement.vue"),
            meta: { requiresAuth: true, requiresAdmin: true },
          },
          // Tambahan route untuk detail device jika diperlukan
          {
            path: "/iot/devices/:id",
            name: "iot-device-detail",
            component: () => import("@/views/dashboard/iot/DeviceDetail.vue"),
            meta: {
              requiresAdmin: true,
              title: "Detail Perangkat IoT",
              breadcrumbs: ["Dashboard", "IoT", "Perangkat", "Detail"],
            },
            props: true,
          },
        ],
      },
      {
        path: "/marketplace",
        redirect: "/marketplace/consumer",
        meta: { requiresAdmin: true },
        children: [
          {
            path: "/marketplace/consumer",
            name: "consumer",
            component: () =>
              import("@/views/dashboard/marketplace/Consumer.vue"),
          },
          {
            path: "/marketplace/farmer",
            name: "farmer",
            component: () => import("@/views/dashboard/marketplace/Farmer.vue"),
          },
          {
            path: "/marketplace/transaction",
            name: "transaction",
            component: () =>
              import("@/views/dashboard/marketplace/Transaction.vue"),
          },
        ],
      },
      {
        path: "/content",
        redirect: "/content/seminar",
        meta: { requiresAdmin: true },
        children: [
          {
            path: "/content/seminar",
            name: "seminar",
            component: () => import("@/views/dashboard/content/Seminar.vue"),
          },
          {
            path: "/content/subscription",
            name: "subscription",
            component: () =>
              import("@/views/dashboard/content/SubscriptionManagement.vue"),
          },
          {
            path: "/content/video",
            name: "video",
            component: () => import("@/views/dashboard/content/Course.vue"),
          },
          {
            path: "/content/article",
            name: "article",
            component: () => import("@/views/dashboard/content/Article.vue"),
          },
          {
            path: "/content/consultation",
            name: "consultation",
            component: () =>
              import("@/views/dashboard/content/Consultation.vue"),
          },
          {
            path: "/content/in-app-banner",
            name: "inappbanner",
            component: () =>
              import("@/views/dashboard/content/InAppBanner.vue"),
          },
          {
            path: "/content/coupon",
            name: "coupon",
            component: () =>
              import("@/views/dashboard/content/CouponManagement.vue"),
          },
          {
            path: "/content/sound-pack",
            name: "soundpack",
            component: () =>
              import("@/views/dashboard/content/SoundPackManagement.vue"),
          },
          {
            path: "/content/segment",
            name: "segment",
            component: () =>
              import("@/views/dashboard/content/SegmentManagement.vue"),
          },
          {
            path: "/content/push-notification",
            name: "pushnotification",
            component: () =>
              import(
                "@/views/dashboard/content/PushNotificationManagement.vue"
              ),
          },
          {
            path: "/content/push-calendar",
            name: "push-calendar",
            component: () =>
              import("@/views/dashboard/content/PushCalendar.vue"),
          },
          {
            path: "/content/communication-inbox",
            name: "communicationinbox",
            component: () =>
              import(
                "@/views/dashboard/content/CommunicationInboxManagement.vue"
              ),
          },
          {
            path: "/content/popup-banner",
            name: "popupbanner",
            component: () =>
              import("@/views/dashboard/content/PopupBannerManagement.vue"),
          },
        ],
      },
      {
        path: "/manufacture",
        redirect: "/manufacture/overview",
        meta: { requiresAdmin: true },
        children: [
          {
            path: "/manufacture/overview",
            name: "manufacture",
            component: () =>
              import("@/views/dashboard/manufacture/Manufacture.vue"),
          },
          {
            path: "/manufacture/raw",
            name: "raw",
            component: () => import("@/views/dashboard/manufacture/Raw.vue"),
          },
          {
            path: "/manufacture/washing",
            name: "washing",
            component: () =>
              import("@/views/dashboard/manufacture/Washing.vue"),
          },
          {
            path: "/manufacture/molding",
            name: "molding",
            component: () =>
              import("@/views/dashboard/manufacture/Molding.vue"),
          },
          {
            path: "/manufacture/printing",
            redirect: "/manufacture/molding",
          },
          {
            path: "/manufacture/clean",
            name: "clean",
            component: () => import("@/views/dashboard/manufacture/Clean.vue"),
          },
        ],
      },
      {
        path: "/setting",
        name: "setting",
        meta: { requiresAdmin: true },
        // component: () => import("@/views/Dashboard.vue"),
        component: () => import("@/views/dashboard/Setting.vue"),
      },
    ],
  },
  {
    path: "/",
    component: () => import("@/components/page-layouts/Auth.vue"),
    children: [
      {
        path: "/sign-in",
        name: "sign-in",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignIn.vue"),
      },
      {
        path: "/sign-up",
        name: "sign-up",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/SignUp.vue"),
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () =>
          import("@/views/crafted/authentication/basic-flow/PasswordReset.vue"),
      },
      {
        path: "/privacy-policy",
        name: "privacy-policy",
        component: () => import("@/views/privacy/PrivacyPolicy.vue"),
      },
    ],
  },
  {
    // Pendaratan tautan bagikan dari aplikasi (https://app.markaswalet.id/l/<rute>):
    // publik, tanpa layout admin. Di Android dengan App Links terverifikasi,
    // tautan ini langsung dibuka aplikasi tanpa lewat halaman ini.
    path: "/l/:path(.*)*",
    name: "open-in-app",
    component: () => import("@/views/share/OpenInApp.vue"),
  },
  {
    // the 404 route, when none of the above matches
    path: "/404",
    name: "404",
    component: () => import("@/views/crafted/authentication/Error404.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  // history: createWebHashHistory(),
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  // reset config to initial state
  store.commit(Mutations.RESET_LAYOUT_CONFIG);

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

  await store.dispatch(
    Actions.AUTH_VERIFY_USER,
    requiresAuth ? next : undefined
  );
  if (!store.state.AnalyticModule.initialized)
    store.dispatch(Actions.ANALYTIC_INIT);

  // Scroll page to top on every route change
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 100);

  if (requiresAuth && !store.state.AuthModule.auth) {
    next("/sign-in");
  } else if (
    requiresAdmin &&
    store.state.AuthModule.user.doc &&
    store.state.AuthModule.user.doc.authorization !== UserAuthorization.admin
  ) {
    next("/farm/harvest");
  } else {
    next();
  }
});

export default router;
